import { useState, useEffect, useRef } from "react";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
} from "react-router";
import { Toaster } from "sonner";
import { ConfirmDialog } from "~/modules/common/ConfirmDialog";
import { initSwUpdate, applyUpdateOnNavigation } from "~/core/pwa/swUpdate";
import { initChunkReload } from "~/core/pwa/chunkReload";

import type { Route } from "./+types/root";
import "./app.css";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { queryPersister } from "~/db/persist/queryPersister";
import { AuthProvider } from "~/domains/auth";
import { BlueDotTransition } from "~/modules/layout/BlueDotTransition";
import { ModulePad } from "~/modules/layout/ModulePad";
import { ThemeApplicator } from "~/core/foundation/ThemeApplicator";
import { initSentry, captureException } from "~/shared/lib/sentry";
import { queryClient } from "~/shared/lib/query-client";
import { initLocalDb } from "~/db/client";
import { useSessionTypesLoader } from "~/core/hooks/useSessionTypesLoader";
import { useSettingsSync } from "~/core/hooks/useSettingsSync";
import { useResilienceInitializer } from "~/core/resilience";
import { SyncInitializer } from "~/core/sync";
import { SyncStatusPanel } from "~/modules/sync";
import { SyncDebugHUD } from "~/modules/dev/SyncDebugHUD";
import { TestPersonaPanel } from "~/core/dev/TestPersonaPanel";
import { DevThemePanel } from "~/core/dev/DevThemePanel";

// Initialize Sentry on app load
initSentry();

// Initialize local database (Dexie or Vault depending on platform)
if (typeof window !== "undefined") {
  initLocalDb();
}

// Dev 모드에서 잔여 Service Worker 자동 제거
// 이전 프로덕션 빌드의 SW가 남아있으면 네비게이션을 가로채서 앱이 깨짐
if (typeof window !== 'undefined' && 'serviceWorker' in navigator && import.meta.env.DEV) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const registration of registrations) {
      registration.unregister();
    }
  });
}

// ISSUE_063: Service Worker 등록 (vite-plugin-pwa, prompt 모드)
// 프로덕션 빌드에서 정적 에셋 프리캐시 + 오프라인 네비게이션 보장.
// 새 버전은 토스트 없이 "안전한 순간"(탭 숨김·라우트 이동)에 조용히 적용한다 — swUpdate 참조.
initSwUpdate();

// 배포 후 stale 청크(옛 해시 파일명) 로드 실패 시 1회 하드 리로드로 자동 복구.
// vite:preloadError 를 잡아 ErrorBoundary 폴백보다 먼저 조용히 복구 — chunkReload 참조.
initChunkReload();

// ISSUE_063: 모든 라우트 모듈을 즉시 프리로드 — 오프라인 네비게이션 보장
// React Router v7은 dynamic import(?import suffix)로 라우트를 lazy-load함.
// import.meta.glob({ eager: true })는 static import라 URL이 달라 캐시가 공유 안 됨.
// eager 없이 glob → 동적 import 함수 생성 → 즉시 실행 → 같은 ?import URL로 캐싱.
const _routeImports = import.meta.glob('./routes/*.tsx');
void Promise.all(Object.values(_routeImports).map(load => load()));

// ============================================================================
// LINKS (Preconnect & Fonts)
// ============================================================================

export const links: Route.LinksFunction = () => [
  { rel: "preconnect", href: "https://cdn.jsdelivr.net" },
  {
    rel: "stylesheet",
    href: "https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/static/pretendard.min.css",
  },
  // Google Fonts (시험지 폰트 선택용)
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;700&family=Noto+Serif+KR:wght@400;700&family=KoPub+Batang:wght@400;700&display=swap",
  },
  // PWA Manifest
  { rel: "manifest", href: "/manifest.json" },
  // iOS Safari PWA Icons
  { rel: "apple-touch-icon", href: "/logo.png" },
];

// ============================================================================
// BOOT LOADER COMPONENT
// ============================================================================

/** Minimum splash screen duration in ms */
const MIN_SPLASH_DURATION = 1500;

/**
 * Note'n Boot Loader
 * Hides layout shift during SSR hydration by showing a full-screen overlay.
 * Shows for a minimum duration to create a branded splash experience.
 * Fades out smoothly once the app is fully mounted AND minimum time has passed.
 */
function BootLoader({ isVisible }: { isVisible: boolean }) {
  // Don't render at all after fade-out completes (for performance)
  const [shouldRender, setShouldRender] = useState(true);
  // Track if minimum splash duration has elapsed
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);

  // Start minimum duration timer on mount
  useEffect(() => {
    const timer = setTimeout(() => setMinTimeElapsed(true), MIN_SPLASH_DURATION);
    return () => clearTimeout(timer);
  }, []);

  // Only hide when BOTH conditions are met: hydration complete AND min time elapsed
  const canHide = !isVisible && minTimeElapsed;

  useEffect(() => {
    if (canHide) {
      // Wait for fade-out transition to complete before unmounting
      const timer = setTimeout(() => setShouldRender(false), 500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [canHide]);

  if (!shouldRender) return null;

  return (
    <div
      className={`
        fixed inset-0 z-[9999]
        bg-surface
        flex flex-col items-center justify-center
        transition-opacity duration-500 ease-out
        ${canHide ? "opacity-0 pointer-events-none" : "opacity-100"}
      `}
    >
      {/* Spinner */}
      <div className="relative w-10 h-10 mb-4">
        <div className="absolute inset-0 border-2 border-line rounded-full" />
        <div className="absolute inset-0 border-2 border-transparent border-t-accent rounded-full animate-spin" />
      </div>

      {/* Loading Text */}
      <span className="text-sm text-subtle font-medium tracking-wide">
        Note'n
      </span>
    </div>
  );
}

// ============================================================================
// SESSION TYPES LOADER COMPONENT
// ============================================================================

/**
 * Loads session types into global store when user is authenticated.
 * Must be rendered inside AuthProvider.
 */
function SessionTypesInitializer() {
  useSessionTypesLoader();
  return null;
}

// ============================================================================
// SETTINGS SYNC COMPONENT
// ============================================================================

/**
 * Syncs user settings (theme, darkMode) with database.
 * Must be rendered inside AuthProvider.
 */
function SettingsSyncInitializer() {
  useSettingsSync();
  return null;
}

// ============================================================================
// RESILIENCE INITIALIZER
// ============================================================================

/**
 * Initializes the connection resilience system.
 * Monitors session health, connection state, and auto-recovers from issues.
 * Must be rendered inside AuthProvider.
 */
function ResilienceInitializer() {
  useResilienceInitializer();
  return null;
}

// ============================================================================
// LOCAL MODE NOTICE (ISSUE_068)
// ============================================================================

import { useWorkspaceStore } from "~/core/store/useWorkspaceStore";
import { useAuthStore } from "~/core/store/useAuthStore";
import { loadSubscriptions } from "~/services/user/subscriptionService";

// ============================================================================
// SUBSCRIPTION LOADER (워크스페이스 전환 시 구독 재로딩)
// ============================================================================

/**
 * Watches workspace changes and reloads subscription data.
 * Initial load is handled by AuthProvider; this handles workspace switches.
 */
function SubscriptionLoader() {
  const active = useWorkspaceStore((s) => s.active);
  const userId = useAuthStore((s) => s.profile?.id);
  const academyId = active.type === 'academy' ? active.id : null;

  useEffect(() => {
    if (!userId) return;
    loadSubscriptions(academyId);
  }, [userId, academyId]);

  return null;
}

// ============================================================================
// ROOT LAYOUT
// ============================================================================

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html
      lang="ko"
      translate="no"
      className="notranslate"
      suppressHydrationWarning
    >
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
        {/* Disable Google Translate popup */}
        <meta name="google" content="notranslate" />
        {/* Security Headers (production only) */}
        {import.meta.env.PROD && (
          <>
            {/*
              🔒 CSP(Content-Security-Policy)는 여기서 렌더하지 않는다 — ISSUE_184-A.

              정책 전문은 `scripts/inject-csp-hashes.mjs`(postbuild)가 **정적 index.html <head>에 직접
              주입**한다. 이유: 이 앱은 정적 SPA(`ssr:false`)라 요청마다 nonce 를 줄 서버가 없어 hash 기반
              CSP 가 정답인데, 인라인 부트스트랩 스크립트의 sha256 해시는 **빌드가 끝나야** 알 수 있다.
              CSP 메타를 React 트리에 두면 클라 하이드레이션 때 해시 없는 원문이 다시 렌더돼 어긋나므로
              (invalid source 에러), React 밖(빌드 산출물)에서만 관리한다. React 19 는 head 의 비관리
              메타를 하이드레이션 때 건드리지 않는다.
              → connect-src 도메인 추가 등 CSP 수정은 root.tsx 가 아니라 그 스크립트에서.

              아래 정적 헤더 2개는 빌드타임 값이 없어 클라/서버 렌더가 동일 → React 트리에 남겨도 안전.
            */}
            <meta httpEquiv="X-Content-Type-Options" content="nosniff" />
            <meta name="referrer" content="strict-origin-when-cross-origin" />
          </>
        )}
        {/* PWA */}
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        <meta name="apple-mobile-web-app-title" content="Note'n" />
        <meta name="theme-color" content="#3b82f6" />
        <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
        <Meta />
        <Links />
      </head>
      <body className="bg-surface text-main">
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

/**
 * PwaUpdater — 라우트가 바뀔 때 대기 중 SW 업데이트를 그 참에 조용히 적용.
 * (탭 숨김 트리거는 swUpdate.initSwUpdate 가 담당. 여기선 "앱 내 이동" 안전-순간만.)
 */
function PwaUpdater() {
  const location = useLocation();
  const firstRef = useRef(true);
  useEffect(() => {
    if (firstRef.current) {
      firstRef.current = false; // 최초 마운트는 "이동"이 아니다
      return;
    }
    applyUpdateOnNavigation();
  }, [location.pathname]);
  return null;
}

// ============================================================================
// APP COMPONENT (Entry Point)
// ============================================================================

export default function App() {
  // Track hydration state to control boot loader
  const [isMounted, setIsMounted] = useState(false);

  // ISSUE_067: Debug HUD — ?debug=1 또는 dev 환경에서 활성화
  const [showDebugHUD, setShowDebugHUD] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setShowDebugHUD(params.get("debug") === "1" || import.meta.env.DEV);
  }, []);

  useEffect(() => {
    // Small delay to ensure layout is stable before revealing
    const timer = setTimeout(() => setIsMounted(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // =========================================================================
  // PREVENT BROWSER BACK/FORWARD NAVIGATION
  // =========================================================================
  // Timeline horizontal scrolling often triggers accidental back gestures.
  // This uses History API to trap navigation within the app.
  useEffect(() => {
    // Push initial state to create a "wall"
    window.history.pushState({ noBack: true }, '', window.location.href);

    const handlePopState = () => {
      // Re-push state to prevent leaving the page
      window.history.pushState({ noBack: true }, '', window.location.href);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: queryPersister,
        maxAge: 1000 * 60 * 60 * 24, // 24h
        // ISSUE_167: 이전 완료 도메인만 오프라인 영속 (students·schools·classrooms·branches·sessionTypes). sessions/messaging 등은 제외
        dehydrateOptions: {
          shouldDehydrateQuery: (query) =>
            ["students", "schools", "classrooms", "branches", "sessionTypes", "questionSets"].includes(query.queryKey[0] as string),
        },
      }}
    >
      {/* Boot Loader - hides layout shift during hydration */}
      <BootLoader isVisible={!isMounted} />

      {/* Theme Applicator - syncs theme state to DOM */}
      <ThemeApplicator />

      {/* PWA 자동 업데이트 - 라우트 이동 시 대기 중 SW 를 조용히 적용 (토스트 없음) */}
      <PwaUpdater />

      {/* Auth Provider - initializes auth state on app boot */}
      <AuthProvider>
        {/* Resilience System - monitors connection, health, and auto-recovers */}
        <ResilienceInitializer />
        {/* Offline Banner - DISABLED: 사용자에게 끊김을 보여주지 않음 */}
        {/* <OfflineBanner /> */}
        {/* Session Types Loader - loads session types after auth */}
        <SessionTypesInitializer />
        {/* Settings Sync - syncs theme settings with database */}
        <SettingsSyncInitializer />
        {/* Subscription Loader - reloads subscriptions on workspace switch */}
        <SubscriptionLoader />
        {/* Local-First Sync Engine - syncs sessions to IndexedDB */}
        <SyncInitializer />
        {/* Sync Status Panel - shows sync progress for initial sync */}
        <SyncStatusPanel />
        {/* Login Intro Animation (Luxo Jr. style) */}
        <BlueDotTransition />
        {/* Module Pad - macOS Launchpad overlay */}
        <ModulePad />
        {/* Main Application */}
        <Outlet />
        {/* ISSUE_067: Sync Debug HUD */}
        {showDebugHUD && <SyncDebugHUD />}
        {/* DEV 칩 (DEV/?debug=1) — 각자 자기 위치를 잡는다(부모 컨테이너에 의존하지 않음). */}
        {showDebugHUD && <TestPersonaPanel />}
        {showDebugHUD && <DevThemePanel />}
      </AuthProvider>

      {/* Toast Notifications (sonner) - bottom-right, auto-dismiss */}
      <Toaster
        position="bottom-right"
        visibleToasts={4}
        gap={8}
        expand={true}
        toastOptions={{
          duration: 3000,
          classNames: {
            toast: "bg-elevated border-line",
            title: "text-main",
            description: "text-muted",
          },
        }}
      />

      {/* Custom Confirm Dialog (replaces window.confirm) */}
      <ConfirmDialog />

      {/* React Query DevTools (dev only) */}
      <ReactQueryDevtools initialIsOpen={false} />
    </PersistQueryClientProvider>
  );
}

// ============================================================================
// HYDRATE FALLBACK (SPA 모드: JS 로딩 중 표시)
// ============================================================================

export function HydrateFallback() {
  return (
    <div className="h-screen w-screen flex flex-col items-center justify-center bg-surface">
      <div className="relative w-10 h-10 mb-4">
        <div className="absolute inset-0 border-2 border-line rounded-full" />
        <div className="absolute inset-0 border-2 border-transparent border-t-accent rounded-full animate-spin" />
      </div>
      <span className="text-sm text-subtle font-medium tracking-wide">Note'n</span>
    </div>
  );
}

// ============================================================================
// ERROR BOUNDARY
// ============================================================================

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details;
  } else if (error && error instanceof Error) {
    // Report to Sentry
    captureException(error, { boundary: 'root' });

    if (import.meta.env.DEV) {
      details = error.message;
      stack = error.stack;
    }
  }

  return (
    <main className="pt-16 p-4 container mx-auto">
      <h1 className="text-2xl font-bold text-main mb-2">
        {message}
      </h1>
      <p className="text-muted mb-4">{details}</p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto bg-elevated rounded-lg text-sm">
          <code className="text-error-ink">{stack}</code>
        </pre>
      )}
    </main>
  );
}
